import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSaldoInGioco} from '../../utils/saldoInGioco';
import {lobbyCasino} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import {eOspite, getCurrentUserId} from '../../utils/currentUser';
import {haLaVoce} from '../../utils/permessi';
import {getWallet} from '../../services/creditApi';
import {avvia, giocoPerId, motivoErrore} from '../../services/casinoApi';
import Nota from '../Vetrina/Nota';
import ErroreGioco from './ErroreGioco';
import {moduloDellaLobby, percorsoSezione, stileColori} from './comune';
import './Casino.css';
import i18next from 'i18next';

/**
 * LA PAGINA DI UN GIOCO: il nome e il fornitore in alto, il gioco sotto,
 * nella cornice, con «Schermo intero».
 *
 * Il gioco lo disegna il FORNITORE: il sito chiede a casino-service
 * l'indirizzo da aprire (avvio) e lo mette nella cornice. Col conto si
 * gioca col saldo vero; con ?prova=1 si prova con crediti finti, solo se il
 * modulo tiene accesa la prova e il fornitore la offre per quel gioco.
 *
 * Da scollegati, senza prova, il gioco non parte: si apre la finestra di
 * accesso e la pagina lo dice.
 */
export default function PaginaGioco({sezione, userService}) {
  useSkin();
  const {t} = useTranslation();
  const {id} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {search} = location;
  const modulo = lobbyCasino(moduloDellaLobby(sezione));
  const percorso = percorsoSezione(sezione);
  const cornice = useRef(null);

  const ospite = eOspite();
  const chiedeProva = new URLSearchParams(search).get('prova') === '1';
  const [gioco, setGioco] = useState(null);
  const [indirizzo, setIndirizzo] = useState('');
  const [errore, setErrore] = useState(null);   // {tipo, codice, tecnico}: vedi ErroreGioco
  const [tentativo, setTentativo] = useState(0);  // «Riprova» richiede l'avvio
  /* IL SIPARIO. Il gioco del fornitore resta nero per secondi mentre si
     collega ai suoi server: invece del nero si vede la copertina del gioco
     con la barra che avanza, e il gioco si carica sotto, nascosto. Si scopre
     quando la pagina del fornitore e' arrivata e sono passati i secondi
     scelti dal backoffice (Moduli › Casinò / Live casino › Caricamento
     all'apertura del gioco); comunque dopo mezzo minuto, se il fornitore
     non dice mai di aver finito. */
  const [arrivato, setArrivato] = useState(false);
  const [scoperto, setScoperto] = useState(false);
  const attesa = Math.max(0, Number(modulo.attesaGioco) || 0);

  useEffect(() => {
    if (!arrivato) return undefined;
    const orologio = setTimeout(() => setScoperto(true), attesa * 1000);
    return () => clearTimeout(orologio);
  }, [arrivato, attesa]);
  useEffect(() => {
    if (!indirizzo) return undefined;
    const orologio = setTimeout(() => setScoperto(true), 30000);
    return () => clearTimeout(orologio);
  }, [indirizzo]);

  useEffect(() => {
    let vivo = true;
    setGioco(null); setIndirizzo(''); setErrore(null); setArrivato(false); setScoperto(false);
    giocoPerId(id)
        .then((g) => {
          if (!vivo) return null;
          setGioco(g);
          const prova = chiedeProva && modulo.prova && g.prova;
          if (!prova && ospite) {
            if (userService && userService.richiediAccesso) userService.richiediAccesso();
            return null;
          }
          return (prova ? Promise.resolve() : controllaCredito()).then(() => avvia(g.id, prova)).then((url) => {
            if (!vivo) return;
            if (url) setIndirizzo(url);
            else setErrore({tipo: 'nonRisponde', codice: '', tecnico: t('Il fornitore non ha mandato l\'indirizzo del gioco.')});
          });
        })
        .catch((e) => vivo && setErrore(motivoErrore(e, ospite)));
    return () => { vivo = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [id, chiedeProva, ospite, tentativo]);

  const inProva = !!(gioco && chiedeProva && modulo.prova && gioco.prova);
  /* Col saldo vero, mentre il gioco e' aperto, il saldo in testata segue le
     giocate (utils/saldoInGioco, modulo Testata › Saldo aggiornato mentre si gioca). */
  useSaldoInGioco(!!indirizzo && !inProva && !ospite, cornice);
  const schermoIntero = () => {
    const el = cornice.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  /* «‹» e' «indietro»: si torna dov'eri (la lobby con la sua scheda, il
     fornitore e la pagina, la categoria della Home, la ricerca), non
     all'inizio della lobby. Aperto da un collegamento, senza un «prima» nel
     sito, porta alla lobby. */
  const indietro = () => (location.key !== 'default' ? navigate(-1) : navigate(percorso));

  return (
    <div className={'cas-pagina'} style={stileColori(modulo.colori)}>
      <div className={'cas-pagina-testa'}>
        <button type={'button'} className={'cas-indietro'} onClick={indietro} aria-label={t('Torna alla lobby')}>‹</button>
        <div className={'cas-titolo'}>
          <small>{gioco ? gioco.fornitore : ''}</small>
          <h1>{gioco ? gioco.nome : t('Gioco')}</h1>
        </div>
        {indirizzo && <span className={'cas-modo' + (inProva ? '' : ' vero')}>{inProva ? t('Prova gratuita') : t('Saldo vero')}</span>}
        {indirizzo && <button type={'button'} className={'cas-intero'} onClick={schermoIntero}>{t('Schermo intero')}</button>}
      </div>

      {errore && (
        <ErroreGioco errore={errore} gioco={gioco} sezione={sezione}
          provaPossibile={!!(gioco && gioco.prova && modulo.prova)}
          onRiprova={() => setTentativo((n) => n + 1)}
          onAccedi={() => userService && userService.richiediAccesso && userService.richiediAccesso()}
          onProva={() => gioco && navigate(`${percorso}/gioco/${encodeURIComponent(gioco.id)}?prova=1`)}
          onLobby={() => navigate(percorso)}
          onVersa={haLaVoce('SITE_DEPOSIT') ? () => navigate('/my-account/cassa') : null}
          onApri={(g) => navigate(`${percorso}/gioco/${encodeURIComponent(g.id)}`)}/>
      )}
      {!errore && gioco && ospite && !inProva && (
        <Nota>
          {gioco.prova && modulo.prova
            ? t('Per giocare serve il conto: entra dalla finestra di accesso, oppure prova gratis.')
            : t('Per giocare serve il conto: entra dalla finestra di accesso.')}
          {gioco.prova && modulo.prova && (
            <button type={'button'} className={'cas-prova in-linea'} onClick={() => navigate(`${percorso}/gioco/${encodeURIComponent(gioco.id)}?prova=1`)}>{t('Prova')}</button>
          )}
        </Nota>
      )}
      {!errore && !(gioco && ospite && !inProva) && (
        /* Sul telefono la cornice prende tutto lo schermo (Casino.css,
           .in-gioco): dentro la pagina, fra testata e barra, il gioco era
           piccolo e scomodo. In alto a sinistra resta il tasto per uscire. */
        <div className={'cas-cornice in-gioco'} ref={cornice}>
          <div className={'cas-gioco-barra'}>
            <button type={'button'} className={'cas-gioco-esci'} onClick={indietro} aria-label={t('Esci dal gioco')}>✕</button>
            <span className={'cas-gioco-nome'}>{gioco ? gioco.nome : ''}</span>
            {indirizzo && <span className={'cas-modo' + (inProva ? '' : ' vero')}>{inProva ? t('Prova gratuita') : t('Saldo vero')}</span>}
          </div>
          {indirizzo && (
            <iframe src={indirizzo} title={gioco ? gioco.nome : t('Gioco')} allow={'fullscreen; autoplay; encrypted-media'} allowFullScreen
              onLoad={() => setArrivato(true)}/>
          )}
          <div className={'cas-sipario' + (scoperto ? ' via' : '')} aria-hidden={scoperto ? 'true' : undefined}
            style={{'--durata': (attesa + 5) + 's'}}>
            {gioco && gioco.copertina && <span className={'cas-sipario-fondo'} style={{backgroundImage: 'url("' + gioco.copertina.replace(/"/g, '%22') + '")'}}/>}
            <div className={'cas-sipario-centro'} role={'status'} aria-live={'polite'}>
              {gioco && gioco.copertina
                ? <img className={'cas-sipario-copertina'} src={gioco.copertina} alt={''}/>
                : <span className={'cas-sipario-copertina vuota'}/>}
              <strong>{gioco ? gioco.nome : ''}</strong>
              <span className={'cas-sipario-barra'}><span/></span>
              <small>{sezione === 'liveCasino' ? t('Ti porto al tavolo') : t('Avvio il gioco')}</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * COL SALDO VERO, PRIMA DI APRIRE: se il conto non ha credito il gioco non si
 * chiede nemmeno. casino-service apre il gioco anche a saldo zero e il
 * fornitore rifiuta poi ogni puntata dentro il gioco, senza dire perche'.
 * Si ferma solo quando il saldo si e' letto ed e' a zero: se la lettura non
 * riesce si prova ad aprire come prima.
 */
function controllaCredito() {
  const id = getCurrentUserId();
  if (!id) return Promise.resolve();
  return getWallet(id).then((conto) => {
    const voci = ['available', 'disponibilita', 'userBalance'];
    const voce = voci.find((v) => conto && conto[v] !== undefined && conto[v] !== null && conto[v] !== '' && Number.isFinite(Number(conto[v])));
    if (voce && Number(conto[voce]) <= 0) {
      const e = new Error(i18next.t('Saldo a zero'));
      e.senzaCredito = true;
      throw e;
    }
  }, () => undefined);
}
